import { ForwardedRef, forwardRef, useCallback, useMemo } from "react";
import Select, {
  SelectInstance,
  OnChangeValue,
  MultiValue,
} from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { keyBy } from "lodash";
import useSelectStyles from "utils/useSelectStyles";
import {
  IdValue,
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import states, { USState } from "utils/usStatesData";
import { UsStateCaProvince } from "__generated__/gql/graphql";

type Option = USState;

function getOptionLabel(option: Option) {
  return option.name;
}

function getOptionValue(option: Option) {
  return option.abbreviation;
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "options" | "value" | "onChange"
> &
  IdValueSelectProps<IsMulti, UsStateCaProvince>;

function USStateSelect<IsMulti extends boolean = false>(
  { isMulti, styles, onChange, value: abbrValue, ...rest }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles(styles);

  const value = useMemo(() => {
    if (!abbrValue) return;

    const optionsByAbbr = keyBy(states, "abbreviation");

    const getOptionForAbbreviation = (v: string) => {
      const option = optionsByAbbr[v];
      if (option) return option;
      return { abbreviation: v, name: v } as USState;
    };

    if (Array.isArray(abbrValue)) {
      return abbrValue.map(getOptionForAbbreviation);
    }

    return getOptionForAbbreviation(abbrValue);
  }, [abbrValue]);

  const handleChange = useCallback(
    (newValue: OnChangeValue<Option, IsMulti>) => {
      onChange?.(
        (isMulti
          ? (newValue as MultiValue<Option>).map(getOptionValue)
          : (newValue && getOptionValue(newValue as Option)) ??
            null) as IdValue<IsMulti, UsStateCaProvince>,
      );
    },
    [onChange, isMulti],
  );

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...{ ref, isMulti, value, getOptionLabel, getOptionValue }}
      options={states}
      onChange={handleChange}
      {...rest}
    />
  );
}

export default forwardRef(USStateSelect);
